import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import SaveNicheButton from "@/components/SaveNicheButton";
import ScanLoader from "@/components/ScanLoader";
import {
  getTopEarners,
  triggerTopEarnersScan,
  getTopEarnersScan,
} from "@/lib/api";

function nFmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function ageLabel(days) {
  if (days == null) return "—";
  if (days < 30) return `${Math.max(1, Math.round(days))}d old`;
  if (days < 365) return `${Math.round(days / 30)}mo old`;
  return `${(days / 365).toFixed(1)}yr old`;
}

function lastUploadLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.max(0, (Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 14) return `${Math.round(days)}d ago`;
  if (days < 60) return `${Math.round(days / 7)}w ago`;
  return d.toLocaleDateString();
}

function durationLabel(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (sec < 60) return `${sec}s`;
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

export default function TopEarners() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getTopEarners();
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load top earners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScan = async (force = false) => {
    setScanning(true);
    try {
      const { scan_id, status, from_cache } = await triggerTopEarnersScan(force);
      if (from_cache && status === "complete") {
        toast.success("Loaded latest cached results");
        await load();
        setScanning(false);
        return;
      }
      toast.info("Scanning YouTube — this takes 30–90s…");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const doc = await getTopEarnersScan(scan_id);
          if (doc.status === "complete") {
            clearInterval(pollRef.current);
            setScanning(false);
            await load();
            toast.success(`Found ${doc.channel_count || doc.channels?.length || 0} top earners`);
          } else if (doc.status === "failed") {
            clearInterval(pollRef.current);
            setScanning(false);
            toast.error(doc.error || "Scan failed");
          }
        } catch {
          clearInterval(pollRef.current);
          setScanning(false);
          toast.error("Lost connection to scan");
        }
      }, 4000);
    } catch (err) {
      setScanning(false);
      toast.error(err?.response?.data?.detail || "Couldn't start scan");
    }
  };

  const channels = data?.channels || [];
  const hasResults = channels.length > 0;
  const stale = data?.stale;
  const age = data?.age_hours;

  return (
    <div data-testid="top-earners-page" className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 1 · TOP EARNERS
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Top 20 small channels by revenue.
      </h1>
      <p className="text-zinc-400 text-sm max-w-2xl mb-6 leading-relaxed">
        We scan YouTube right now for channels under{" "}
        <span className="text-white">100k subs</span> that are{" "}
        <span className="text-white">under 2 years old</span> and rank them by
        estimated monthly revenue. These are the realistic role models — small
        creators making real money today. Results cached for 24h.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <button
          data-testid="te-scan-btn"
          onClick={() => runScan(false)}
          disabled={scanning}
          className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
        >
          {scanning ? "Scanning YouTube…" : hasResults ? "▸ Refresh scan" : "▸ Run scan"}
        </button>
        {hasResults && (
          <button
            data-testid="te-force-rescan-btn"
            onClick={() => runScan(true)}
            disabled={scanning}
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
          >
            ↻ Force fresh scan
          </button>
        )}
        {hasResults && age != null && (
          <span
            className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
              stale ? "text-amber-400" : "text-zinc-500"
            }`}
            data-testid="te-cache-age"
          >
            cached {age < 1 ? `${Math.round(age * 60)}m` : `${age}h`} ago{stale ? " · stale" : ""}
          </span>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading<span className="blink">_</span>
        </div>
      ) : scanning ? (
        <div className="border border-zinc-800 rounded-sm py-6" data-testid="te-scanning">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-4 text-center">
            // HUNTING SMALL CHANNELS WITH BIG REVENUE
          </div>
          <ScanLoader
            stages={[
              "Querying 6 seed buckets",
              "Resolving channels from top videos",
              "Pulling stats · filtering by sub count",
              "Filtering by channel age (60d–2yr)",
              "Sampling uploads · format-aware RPM",
              "Ranking by est. monthly revenue",
            ]}
            stageDurationMs={6000}
            subtitle="~60–90s · burns ~1,200 quota units"
            logLines={[
              "search.list  q='personal finance side hustle' · order=viewCount",
              "search.list  q='ai automation business 2026'",
              "search.list  q='stoic philosophy modern life'",
              "search.list  q='productivity systems for creators'",
              "→ aggregated unique channel ids",
              "channels.list  part=snippet,statistics,contentDetails",
              "filter  1k ≤ subs ≤ 100k",
              "filter  60d ≤ age ≤ 2yr",
              "filter  videos ≥ 3 · views ≥ 1000",
              "playlistItems.list  → recent 10 uploads",
              "videos.list  → duration sampling",
              "→ classify shorts vs long-form",
              "long-form RPM $3 · shorts RPM $0.07",
              "filter  rev ≥ $200/mo",
              "→ sorting by est_monthly_revenue desc",
            ]}
          />
        </div>
      ) : !hasResults ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="te-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            // NO SCAN YET
          </div>
          <p className="text-zinc-400 text-sm mb-6">
            Click <b>Run scan</b> above to find the top earners. Takes about a
            minute and burns ~1,200 quota units.
          </p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="te-list">
          <div className="grid grid-cols-12 px-4 py-3 bg-zinc-900 border-b-2 border-yellow-400/30 font-mono text-xs uppercase tracking-[0.15em] text-yellow-400 font-bold">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-3">Channel</div>
            <div className="col-span-2 text-center">Rev / mo</div>
            <div className="col-span-1 text-center">Subs</div>
            <div className="col-span-1 text-center">Uploads / mo</div>
            <div className="col-span-1 text-center">Avg length</div>
            <div className="col-span-1 text-center">Age</div>
            <div className="col-span-1 text-center">Last</div>
            <div className="col-span-1 text-center">Save</div>
          </div>
          {channels.map((c, i) => (
            <div
              key={c.channel_id}
              data-testid={`te-row-${c.channel_id}`}
              className="grid grid-cols-12 px-4 py-4 border-b border-zinc-900 last:border-b-0 items-center hover:bg-zinc-950/60 transition-colors recon-row-in"
              style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
            >
              <div className="col-span-1 text-center font-display text-2xl font-black text-yellow-400">
                {String(i + 1).padStart(2, "0")}
              </div>
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="col-span-3 flex items-center gap-3 min-w-0 hover:text-yellow-400"
              >
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt={c.name} className="h-10 w-10 rounded-full shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-zinc-800 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-zinc-300 truncate">{c.description}</div>
                </div>
              </a>
              <div className="col-span-2 text-center font-display text-xl font-bold text-emerald-300">
                ${nFmt(c.est_monthly_revenue)}
              </div>
              <div className="col-span-1 text-center text-sm font-mono text-zinc-100">
                {nFmt(c.subscribers)}
              </div>
              <div className="col-span-1 text-center">
                <div className="text-sm font-mono text-zinc-100">
                  {c.uploads_per_month != null ? c.uploads_per_month : "—"}
                </div>
                <div className="text-[11px] font-mono text-zinc-400">
                  {c.video_count} total
                </div>
                {c.est_total_shorts != null && (c.est_total_shorts > 0 || c.est_total_longs > 0) && (
                  <div className="text-[10px] font-mono mt-0.5 flex items-center justify-center gap-1.5">
                    <span className="text-amber-400">{c.est_total_shorts}s</span>
                    <span className="text-zinc-700">·</span>
                    <span className="text-emerald-400">{c.est_total_longs}L</span>
                  </div>
                )}
              </div>
              <div className="col-span-1 text-center text-sm font-mono text-zinc-100">
                <div>{durationLabel(c.avg_video_length_sec)}</div>
                {c.format && c.format !== "unknown" && (
                  <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${
                    c.format === "shorts" ? "text-amber-400" : "text-emerald-400"
                  }`}>
                    {c.format === "shorts" ? "Shorts" : "Long-form"}
                  </div>
                )}
              </div>
              <div className="col-span-1 text-center text-xs font-mono text-zinc-200">
                {ageLabel(c.channel_age_days)}
              </div>
              <div className="col-span-1 text-center text-xs font-mono text-zinc-200">
                {lastUploadLabel(c.last_upload)}
              </div>
              <div className="col-span-1 text-center">
                <SaveNicheButton channel={c} source="top_earners" />
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 leading-relaxed">
        revenue est = avg_monthly_views × RPM ÷ 1000<br />
        long-form rpm = $3 · shorts rpm = $0.07 · real RPM varies by niche / country
      </p>
    </div>
  );
}
